import { Plus } from 'lucide-react'
import { HOLIDAYS_2026, HOLIDAY_TYPE_LABELS, checkUserLeave, checkCompanyEvent } from '../../utils/dashboardConstants'

export default function DashboardCalendar({ currentDate, projects, userLeaves, companyEvents, userInfo, onAddEvent }) {
  const today = currentDate
  const year = today.getFullYear()
  const month = today.getMonth()
  const firstDay = new Date(year, month, 1).getDay()
  const daysInMonth = new Date(year, month + 1, 0).getDate()
  const todayDate = new Date().getDate()
  const todayMonth = new Date().getMonth()
  const todayYear = new Date().getFullYear()

  const renderDays = () => {
    const days = []

    // Empty cells before first day
    for (let i = 0; i < firstDay; i++) {
      days.push(<div key={`empty-${i}`} className="aspect-square"></div>)
    }

    // Days of month
    for (let day = 1; day <= daysInMonth; day++) {
      const isToday = day === todayDate && month === todayMonth && year === todayYear
      const dateKey = `${year}-${month + 1}-${day}`
      const dateObj = new Date(year, month, day)
      const systemHoliday = HOLIDAYS_2026[dateKey]
      const personalLeave = checkUserLeave(dateObj, userLeaves)
      const compEvent = checkCompanyEvent(dateObj, companyEvents)

      const holiday = compEvent || personalLeave || systemHoliday
      const dayOfWeek = dateObj.getDay()
      const isSunday = dayOfWeek === 0
      const isSaturday = dayOfWeek === 6

      days.push(
        <div
          key={day}
          className={`
            aspect-square flex flex-col items-center justify-center rounded-lg text-xs font-medium transition-all cursor-pointer relative group
            ${holiday
              ? `${holiday.color} text-gray-700 shadow-sm hover:scale-105 hover:shadow-md`
              : isSunday
                ? 'bg-red-50 text-red-600 hover:bg-red-100'
                : isSaturday
                  ? 'bg-orange-50 text-orange-600 hover:bg-orange-100'
                  : 'bg-white text-gray-700 hover:bg-blue-50 hover:text-blue-600'
            }
            ${isToday ? 'ring-1 ring-green-400/30 bg-green-50/30' : ''}
          `}
          title={holiday ? holiday.name : ''}
        >
          {isToday && (
            <div className="absolute top-1.5 right-1.5 w-1.5 h-1.5 bg-green-500 rounded-full shadow-sm animate-pulse"></div>
          )}

          <span className={`relative z-10 ${isToday ? 'font-extrabold text-green-700 text-sm' : holiday ? 'font-bold' : ''}`}>
            {day}
          </span>

          {holiday && (
            <div className="w-1 h-1 bg-gray-600 rounded-full mt-0.5 opacity-60 shadow-sm"></div>
          )}

          {holiday && (
            <div className="absolute bottom-full mb-2 hidden group-hover:block z-20 bg-gray-900 text-white text-[10px] px-2 py-1.5 rounded-lg whitespace-nowrap shadow-xl border border-gray-700">
              <div className="font-semibold">{holiday.name}</div>
              <div className="text-gray-300 text-[9px] mt-0.5">
                {holiday.type === 'companyevent'
                  ? `${HOLIDAY_TYPE_LABELS[holiday.type]}${holiday.description ? ': ' + holiday.description : ''}`
                  : HOLIDAY_TYPE_LABELS[holiday.type] || ''
                }
              </div>
              <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-900"></div>
            </div>
          )}
        </div>
      )
    }

    return days
  }

  return (
    <div className="lg:col-span-2 relative backdrop-blur-xl bg-white/70 shadow-xl rounded-3xl border border-white/40 p-4 overflow-hidden">
      {/* Shimmer Effect */}
      <div className="absolute inset-0 rounded-3xl bg-gradient-to-r from-transparent via-white/20 to-transparent animate-shimmer pointer-events-none"></div>

      <div className="relative flex items-center justify-between mb-8">
        <h2 className="text-base font-bold bg-gradient-to-r from-blue-600 to-blue-600 bg-clip-text text-transparent flex items-center gap-4">
          <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={4} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
          Kalender
        </h2>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 text-xs text-slate-600 font-medium">
            <span>{currentDate.toLocaleDateString('id-ID', { month: 'long', year: 'numeric' })}</span>
          </div>
          {userInfo?.role === 'admin' && (
            <button
              onClick={onAddEvent}
              className="p-1.5 bg-blue-50 text-blue-600 hover:bg-blue-100 rounded-lg transition-colors flex items-center gap-1"
              title="Tambah Acara Perusahaan"
            >
              <Plus className="w-4 h-4" />
              <span className="text-xs font-semibold hidden sm:inline">Acara</span>
            </button>
          )}
        </div>
      </div>

      {/* Calendar Grid */}
      <div className="relative bg-gradient-to-br from-blue-50/50 via-blue-50/50 to-blue-50/50 rounded-2xl p-2 backdrop-blur-sm border border-white/40">
        <div className="grid grid-cols-7 gap-0.5 mb-1">
          {['Min', 'Sen', 'Sel', 'Rab', 'Kam', 'Jum', 'Sab'].map((day) => (
            <div key={day} className="text-center text-[10px] font-bold text-gray-600 py-0.5">
              {day}
            </div>
          ))}
        </div>

        <div className="grid grid-cols-7 gap-0.5">
          {renderDays()}
        </div>

        <div className="grid grid-cols-2 gap-1.5 mt-2 pt-2 border-t border-blue-200">
          <div className="flex items-center gap-1">
            <div className="w-1.5 h-1.5 bg-green-500 rounded-full mb-0.5 animate-pulse"></div>
            <span className="text-[9px] text-gray-600">Hari Ini</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-2 h-2 bg-rose-400 rounded"></div>
            <span className="text-[9px] text-gray-600">Libur Nasional</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-2 h-2 bg-emerald-400 rounded"></div>
            <span className="text-[9px] text-gray-600">Hari Besar (Agama)</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-2 h-2 bg-sky-300 rounded"></div>
            <span className="text-[9px] text-gray-600">Cuti Bersama</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-2 h-2 bg-indigo-300 rounded"></div>
            <span className="text-[9px] text-gray-600">Cuti Anda</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-2 h-2 bg-purple-300 rounded"></div>
            <span className="text-[9px] text-gray-600">Izin Sakit Anda</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-2 h-2 bg-teal-400 rounded"></div>
            <span className="text-[9px] text-gray-600">Acara Perusahaan</span>
          </div>
        </div>
      </div>

      {/* Quick Stats Below Calendar */}
      <div className="grid grid-cols-3 gap-2 mt-2">
        <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-1.5 text-center">
          <div className="text-lg font-bold text-blue-600">{new Date().getDate()}</div>
          <div className="text-[10px] text-gray-600 mt-0.5">Hari Ini</div>
        </div>
        <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-1.5 text-center">
          <div className="text-lg font-bold text-blue-600">{projects.length}</div>
          <div className="text-[10px] text-gray-600 mt-0.5">Projects</div>
        </div>
        <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-lg p-1.5 text-center">
          <div className="text-lg font-bold text-green-600">{new Date().toLocaleDateString('id-ID', { weekday: 'long' }).slice(0, 3)}</div>
          <div className="text-[10px] text-gray-600 mt-0.5">Hari</div>
        </div>
      </div>
    </div>
  )
}
